import React, { useMemo, useState } from 'react';
import {
  Settings,
  User,
  MapPin,
  CreditCard,
  Users,
  LogOut,
  Shield,
  Building2,
  Landmark,
  Percent,
  MessageCircle,
  BookOpen,
  Languages,
  Type,
  Palette,
  ChevronRight,
  ArrowLeft,
  Trash2,
  CalendarRange,
  type LucideIcon,
} from 'lucide-react';
import ProfileSettings from './ProfileSettings';
import DeleteAccountSettings from './DeleteAccountSettings';
import LineSettings from './LineSettings';
import PaymentSettings from './PaymentSettings';
import OwnersSettings from './OwnersSettings';
import WorkspaceAdminsSettings from './WorkspaceAdminsSettings';
import CompanySettings from './CompanySettings';
import LedgerSettings from './LedgerSettings';
import FinancialYearSettings from './FinancialYearSettings';
import BanksSettings from './BanksSettings';
import FinanceDefaultsSettings from './FinanceDefaultsSettings';
import SmsReminderSettings from './SmsReminderSettings';
import SubscriptionSettings from './SubscriptionSettings';
import LanguageSettings from './LanguageSettings';
import FontSettings from './FontSettings';
import ThemeSettings from './ThemeSettings';
import { useNavigate } from 'react-router-dom';
import { clearAuthAndTenant } from '../../lib/tenant';
import { authApi } from '../../api';
import { canAccessSettingsSection } from '../../lib/permissions';
import { useTranslation } from '../../lib/i18n';

interface SettingsLayoutProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  setActiveItem: (item: string) => void;
  children?: React.ReactNode;
}

type Accent = 'emerald' | 'sky' | 'violet' | 'amber' | 'teal' | 'rose' | 'indigo';

interface SectionDef {
  id: string;
  label: string;
  desc: string;
  icon: LucideIcon;
  group: string;
  accent: Accent;
}

const GROUP_KEYS = [
  'settings.groupOrganization',
  'settings.groupOperations',
  'settings.groupPeople',
  'settings.groupAccount',
] as const;

const ACCENT_CHIP: Record<Accent, string> = {
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
  sky: 'bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300',
  violet: 'bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
  teal: 'bg-teal-100 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300',
};

function SettingsLayout({ activeSection, onSectionChange }: SettingsLayoutProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const sections = useMemo<SectionDef[]>(
    () => [
      { id: 'company', label: t('settings.company'), desc: t('settings.companyDesc'), icon: Building2, group: t('settings.groupOrganization'), accent: 'emerald' },
      { id: 'ledgers', label: t('settings.ledgers'), desc: t('settings.ledgersDesc'), icon: BookOpen, group: t('settings.groupOrganization'), accent: 'indigo' },
      { id: 'financialYear', label: t('settings.financialYear'), desc: t('settings.financialYearDesc'), icon: CalendarRange, group: t('settings.groupOrganization'), accent: 'teal' },
      { id: 'line', label: t('settings.line'), desc: t('settings.lineDesc'), icon: MapPin, group: t('settings.groupOperations'), accent: 'violet' },
      { id: 'banks', label: t('settings.banks'), desc: t('settings.banksDesc'), icon: Landmark, group: t('settings.groupOperations'), accent: 'amber' },
      { id: 'finance', label: t('settings.finance'), desc: t('settings.financeDesc'), icon: Percent, group: t('settings.groupOperations'), accent: 'emerald' },
      { id: 'sms', label: t('settings.sms'), desc: t('settings.smsDesc'), icon: MessageCircle, group: t('settings.groupOperations'), accent: 'teal' },
      { id: 'payment', label: t('settings.payment'), desc: t('settings.paymentDesc'), icon: CreditCard, group: t('settings.groupOperations'), accent: 'teal' },
      { id: 'owners', label: t('settings.owners'), desc: t('settings.ownersDesc'), icon: Users, group: t('settings.groupPeople'), accent: 'rose' },
      { id: 'admins', label: t('settings.admins'), desc: t('settings.adminsDesc'), icon: Shield, group: t('settings.groupPeople'), accent: 'indigo' },
      { id: 'subscription', label: t('settings.subscription'), desc: t('settings.subscriptionDesc'), icon: CreditCard, group: t('settings.groupAccount'), accent: 'emerald' },
      { id: 'language', label: t('settings.language'), desc: t('settings.languageDesc'), icon: Languages, group: t('settings.groupAccount'), accent: 'sky' },
      { id: 'font', label: t('settings.font'), desc: t('settings.fontDesc'), icon: Type, group: t('settings.groupAccount'), accent: 'violet' },
      { id: 'theme', label: t('settings.theme'), desc: t('settings.themeDesc'), icon: Palette, group: t('settings.groupAccount'), accent: 'indigo' },
      { id: 'profile', label: t('settings.profile'), desc: t('settings.profileDesc'), icon: User, group: t('settings.groupAccount'), accent: 'sky' },
      { id: 'deleteAccount', label: t('settings.deleteAccount'), desc: t('settings.deleteAccountDesc'), icon: Trash2, group: t('settings.groupAccount'), accent: 'rose' },
    ],
    [t]
  );

  const groupOrder = useMemo(
    () => GROUP_KEYS.map((key) => t(key)),
    [t]
  );

  const visibleSections = sections.filter((s) => canAccessSettingsSection(s.id));
  const active = visibleSections.find((s) => s.id === activeSection) ?? visibleSections[0] ?? sections[0];
  const ActiveIcon = active.icon;

  // Mobile uses a two-level flow: a "Settings home" list → tap to open a section.
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    authApi.logout().catch(() => {});
    clearAuthAndTenant();
    navigate('/login');
  };

  const openMobile = (id: string) => {
    onSectionChange(id);
    setMobileOpen(true);
  };

  const renderSection = () => {
    switch (active.id) {
      case 'company':
        return <CompanySettings />;
      case 'ledgers':
        return <LedgerSettings />;
      case 'financialYear':
        return <FinancialYearSettings />;
      case 'profile':
        return <ProfileSettings />;
      case 'deleteAccount':
        return <DeleteAccountSettings />;
      case 'language':
        return <LanguageSettings />;
      case 'font':
        return <FontSettings />;
      case 'theme':
        return <ThemeSettings />;
      case 'subscription':
        return <SubscriptionSettings />;
      case 'line':
        return <LineSettings />;
      case 'banks':
        return <BanksSettings />;
      case 'finance':
        return <FinanceDefaultsSettings />;
      case 'sms':
      case 'whatsapp':
        return <SmsReminderSettings />;
      case 'payment':
        return <PaymentSettings />;
      case 'owners':
        return <OwnersSettings />;
      case 'admins':
        return <WorkspaceAdminsSettings />;
      default:
        return <CompanySettings />;
    }
  };

  const contentCard = (
    <div className="app-card min-w-0 overflow-hidden">
      <div className="flex items-center gap-3 border-b border-zinc-200 bg-gradient-to-r from-zinc-50 to-white px-4 py-4 dark:border-zinc-700 dark:from-zinc-900 dark:to-zinc-900 sm:px-6">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${ACCENT_CHIP[active.accent]}`}>
          <ActiveIcon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold text-zinc-900 dark:text-zinc-50 sm:text-lg">{active.label}</h2>
          <p className="truncate text-xs text-zinc-500 dark:text-zinc-400 sm:text-sm">
            {active.desc} · {t('common.appliesWorkspace')}
          </p>
        </div>
      </div>
      <div className="app-card-inner min-w-0">{renderSection()}</div>
    </div>
  );

  return (
    <div className="app-page">
      {/* ===================== MOBILE (two-level) ===================== */}
      <div className="space-y-4 lg:hidden">
        {!mobileOpen ? (
          <>
            {/* Hero */}
            <header className="relative overflow-hidden rounded-2xl border border-zinc-200/90 bg-gradient-to-br from-emerald-600 to-teal-600 px-5 py-6 text-white shadow-md">
              <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" aria-hidden />
              <div className="relative flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur">
                  <Settings className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-50/90">{t('settings.workspace')}</p>
                  <h1 className="text-xl font-semibold tracking-tight">{t('settings.title')}</h1>
                </div>
              </div>
              <p className="relative mt-3 text-sm leading-relaxed text-emerald-50/90">{t('settings.subtitle')}</p>
            </header>

            {/* Grouped list */}
            {groupOrder.map((group) => {
              const items = visibleSections.filter((s) => s.group === group);
              if (!items.length) return null;
              return (
                <div key={group}>
                  <p className="mb-1.5 px-1 text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-400 dark:text-zinc-500">{group}</p>
                  <div className="app-card divide-y divide-zinc-100 overflow-hidden dark:divide-zinc-700">
                    {items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => openMobile(item.id)}
                          className="flex w-full items-center gap-3 px-3.5 py-3.5 text-left transition-colors active:bg-zinc-50 dark:active:bg-zinc-800"
                        >
                          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${ACCENT_CHIP[item.accent]}`}>
                            <Icon className="h-5 w-5" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">{item.label}</span>
                            <span className="block truncate text-xs text-zinc-500 dark:text-zinc-400">{item.desc}</span>
                          </span>
                          <ChevronRight className="h-5 w-5 shrink-0 text-zinc-300 dark:text-zinc-600" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-rose-200 bg-white px-3 py-3 text-sm font-semibold text-rose-700 shadow-sm transition-colors active:bg-rose-50 dark:border-rose-800/60 dark:bg-zinc-900 dark:text-rose-400 dark:active:bg-rose-950/40"
            >
              <LogOut className="h-[18px] w-[18px]" />
              {t('common.logout')}
            </button>
          </>
        ) : (
          <>
            {/* Sticky back bar */}
            <div className="sticky top-0 z-10 -mx-3 flex items-center gap-3 border-b border-zinc-200 bg-white px-3 py-2.5 dark:border-zinc-700 dark:bg-zinc-900 sm:mx-0 sm:px-0">
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-600 shadow-sm active:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 dark:active:bg-zinc-700"
                aria-label="Back to all settings"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-400">{active.group}</p>
                <h1 className="truncate text-base font-semibold text-zinc-900 dark:text-zinc-100">{active.label}</h1>
              </div>
            </div>

            {contentCard}
          </>
        )}
      </div>

      {/* ===================== DESKTOP ===================== */}
      <div className="hidden space-y-5 lg:block">
        <header className="relative overflow-hidden rounded-2xl border border-zinc-200/90 bg-gradient-to-r from-emerald-50 via-white to-teal-50/60 px-5 py-6 shadow-sm ring-1 ring-zinc-950/[0.03] dark:border-zinc-700 dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-900 dark:ring-zinc-700/50 sm:px-7">
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-emerald-500/10 blur-2xl" aria-hidden />
          <div className="relative flex items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow-md shadow-emerald-600/30">
              <Settings className="h-6 w-6" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400">{t('settings.workspace')}</p>
              <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">{t('settings.title')}</h1>
              <p className="mt-0.5 text-sm text-zinc-600 dark:text-zinc-400">{t('settings.subtitleLong')}</p>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <aside className="lg:col-span-4 xl:col-span-3">
            <div className="sticky top-4 space-y-3">
              <nav className="app-card overflow-hidden p-2" aria-label="Settings sections">
                {groupOrder.map((group) => {
                  const items = visibleSections.filter((s) => s.group === group);
                  if (!items.length) return null;
                  return (
                    <div key={group} className="mb-1 last:mb-0">
                      <p className="px-2.5 pb-1 pt-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-400 dark:text-zinc-500">{group}</p>
                      {items.map((item) => {
                        const Icon = item.icon;
                        const on = item.id === active.id;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => onSectionChange(item.id)}
                            aria-current={on ? 'page' : undefined}
                            className={`group flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition-all ${
                              on ? 'bg-emerald-50 ring-1 ring-emerald-200/80 dark:bg-emerald-950/40 dark:ring-emerald-800/50' : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/80'
                            }`}
                          >
                            <span
                              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors ${
                                on ? ACCENT_CHIP[item.accent] : 'bg-zinc-100 text-zinc-500 group-hover:bg-zinc-200/70 dark:bg-zinc-800 dark:text-zinc-400 dark:group-hover:bg-zinc-700'
                              }`}
                            >
                              <Icon className="h-[18px] w-[18px]" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className={`block truncate text-sm font-semibold ${on ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-700 dark:text-zinc-300'}`}>
                                {item.label}
                              </span>
                              <span className="block truncate text-xs text-zinc-500 dark:text-zinc-400">{item.desc}</span>
                            </span>
                            <ChevronRight
                              className={`h-4 w-4 shrink-0 transition-all ${
                                on ? 'text-emerald-600 dark:text-emerald-400' : 'text-zinc-300 group-hover:translate-x-0.5 group-hover:text-zinc-400 dark:text-zinc-600 dark:group-hover:text-zinc-400'
                              }`}
                            />
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </nav>

              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-rose-200 bg-white px-3 py-2.5 text-sm font-semibold text-rose-700 shadow-sm transition-colors hover:bg-rose-50 dark:border-rose-800/60 dark:bg-zinc-900 dark:text-rose-400 dark:hover:bg-rose-950/40"
              >
                <LogOut className="h-[18px] w-[18px]" />
                {t('common.logout')}
              </button>
            </div>
          </aside>

          <section className="lg:col-span-8 xl:col-span-9">{contentCard}</section>
        </div>
      </div>
    </div>
  );
}

export default SettingsLayout;
